import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { Alert, Badge, Button, Form, Spinner } from 'react-bootstrap';
import { fetchAllotedStationUsers, clearStationGroupsError } from '../../../store/slices/stationGroupsSlice';
import { impersonateStationUser } from '../../../store/slices/authSlice';
import { resetChatState } from '../../../store/slices/chatSlice';
import { resetPollState } from '../../../store/slices/pollSlice';
import { resetContestState } from '../../../store/slices/contestSlice';
import { getDefaultPath } from '../../../helpers/roleUtils';
import { STATION_GROUPS } from '../../../constants';
import { toast } from 'react-toastify';
import LoginIcon from '@mui/icons-material/Login';
import RadioIcon from '@mui/icons-material/Radio';
import SearchIcon from '@mui/icons-material/Search';
import './StationGroups.css';


const StationGroups = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const otherUser = useSelector((state) => state.auth.otherUser);
  const { users, loading, error, lastFetchedAllotedStationGroups } = useSelector((state) => state.stationGroups);
  const [loginAsLoading, setLoginAsLoading] = React.useState(null);
  const [aliasSearch, setAliasSearch] = useState('');

  const isGroupAdmin = (u) => (u?.attributes?.roles || []).includes('group_admin');
  const effectiveGroupAdmin = isGroupAdmin(user) ? user : (isGroupAdmin(otherUser) ? otherUser : null);
  const getAllotedStationGroups = (adminUser) => {
    const direct = adminUser?.attributes?.allotedStationGroups;
    if (typeof direct === 'string' && direct.trim()) return direct;

    const metadata = adminUser?.attributes?.metadata;
    const fromKnownKey = metadata?.['3']?.allotedStationGroups;
    if (typeof fromKnownKey === 'string' && fromKnownKey.trim()) return fromKnownKey;

    if (metadata && typeof metadata === 'object') {
      const nestedMatch = Object.values(metadata).find(
        (entry) => typeof entry?.allotedStationGroups === 'string' && entry.allotedStationGroups.trim()
      );
      if (nestedMatch?.allotedStationGroups) return nestedMatch.allotedStationGroups;
    }

    return '';
  };

  const allotedStationGroups = getAllotedStationGroups(effectiveGroupAdmin);

  // Only fetch when data not already loaded for this allotted list (e.g. fresh refresh); reuse when visiting Switch station again
  useEffect(() => {
    if (!allotedStationGroups || !effectiveGroupAdmin) return;
    if (lastFetchedAllotedStationGroups === allotedStationGroups && users.length >= 0) return; // already have data for this list
    dispatch(fetchAllotedStationUsers(allotedStationGroups));
  }, [dispatch, allotedStationGroups, effectiveGroupAdmin, lastFetchedAllotedStationGroups, users.length]);

  const handleLoginAs = async (row) => {
    const stationId = row.id || row.attributes?.id;
    if (!stationId) return;
    setLoginAsLoading(stationId);
    try {
      await dispatch(impersonateStationUser(row)).unwrap();
      dispatch(resetChatState());
      dispatch(resetPollState());
      dispatch(resetContestState());
      toast.success(`Logged in as ${row.attributes?.stationName || stationId}`, {
        position: 'top-right',
        autoClose: 3000,
      });
      const path = getDefaultPath({ attributes: { roles: ['admin'] } });
      navigate(path);
    } catch (err) {
      toast.error(err || 'Login as failed. The impersonation API may not be available yet.', {
        position: 'top-right',
        autoClose: 5000,
      });
    } finally {
      setLoginAsLoading(null);
    }
  };

  const getStationLogo = (row) => {
    const url = row.attributes?.avatar || row.attributes?.stationLogo || row.attributes?.logo;
    return url;
  };

  const getUsername = (row) => {
    const meta = row.attributes?.metadata?.['3'];
    if (meta?.firstName || meta?.lastName) {
      return [meta.firstName, meta.lastName].filter(Boolean).join(' ');
    }
    if (meta?.email) return meta.email;
    return row.id || row.attributes?.id || '—';
  };

  const filteredUsers = useMemo(() => {
    if (!aliasSearch.trim()) return users;
    const term = aliasSearch.trim().toLowerCase();
    return users.filter((row) => {
      const alias = (getUsername(row) || '').toLowerCase();
      const stationName = (row.attributes?.stationSchool || '').toLowerCase();
      return alias.includes(term) || stationName.includes(term);
    });
  }, [users, aliasSearch]);

  const columns = [
    {
      name: 'Logo',
      cell: (row) => {
        const logoUrl = getStationLogo(row);
        return (
          <div className="d-flex align-items-center justify-content-center" style={{ width: 48, height: 48 }}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                style={{ maxWidth: 48, maxHeight: 48, objectFit: 'contain', borderRadius: 4 }}
              />
            ) : (
              <div
                className="bg-secondary text-white rounded d-flex align-items-center justify-content-center fw-bold"
                style={{ width: 48, height: 48, fontSize: '1rem' }}
              >
                {(row.attributes?.stationSchool || row.id || '?').toString().charAt(0).toUpperCase()}
              </div>
            )}
          </div>
        );
      },
      width: '80px',
      center: true,
    },
    {
      name: 'Station Name',
      selector: (row) => row.attributes?.stationSchool || '—',
      sortable: true,
      wrap: true,
    },
    {
      name: 'Alias',
      cell: (row) => {
        const name = getUsername(row);
        const avatarUrl = row.attributes?.avatar;
        return (
          <div className="d-flex align-items-center">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt=""
                className="rounded-circle me-2"
                style={{ width: 32, height: 32, objectFit: 'cover' }}
              />
            ) : (
              <div
                className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-2"
                style={{ width: 32, height: 32, fontSize: '0.85rem' }}
              >
                {name.charAt(0).toUpperCase()}
              </div>
            )}
            <span>{name}</span>
          </div>
        );
      },
      sortable: true,
      wrap: true,
    },
    {
      name: 'Station Groups',
      cell: (row) => {
        const sg = row.attributes?.stationGroup;
        if (!sg) return '—';
        const groups = sg.split(',').map((s) => s.trim()).filter(Boolean);
        return (
          <div className="d-flex flex-wrap gap-1">
            {groups.map((group) => (
              <Badge key={group} bg="primary" className="stations-list-group-badge">
               {group}
              </Badge>
            ))}
          </div>
        );
      },
      sortable: true,
      width: '180px',
    },
    {
      name: 'Actions',
      cell: (row) => {
        const stationId = row.id || row.attributes?.id;
        const busy = loginAsLoading === stationId;
        return (
          <Button
            size="sm"
            variant="primary"
            className="stations-list-login-btn"
            onClick={() => handleLoginAs(row)}
            disabled={busy}
          >
            {busy ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Logging in...
              </>
            ) : (
              <>
                <LoginIcon fontSize="small" className="me-1" />
                Login
              </>
            )}
          </Button>
        );
      },
      width: '160px',
      center: true,
    },
  ];

  const tableCustomStyles = {
    table: {
      style: {
        borderRadius: '12px',
        overflow: 'hidden',
      },
    },
    tableWrapper: {
      style: {
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#f8f9fa',
        fontSize: '13px',
        fontWeight: 600,
        color: '#495057',
        minHeight: '52px',
        borderBottom: '2px solid #e9ecef',
      },
    },
    rows: {
      style: {
        minHeight: '64px',
        fontSize: '14px',
        '&:hover': {
          backgroundColor: '#f8f9fa',
        },
      },
    },
  };

  if (!effectiveGroupAdmin) {
    return (
      <div className="stations-list-page">
        <div className="stations-list-card">
          <Alert variant="warning" className="mb-0">You do not have access to this page.</Alert>
        </div>
      </div>
    );
  }

  if (!allotedStationGroups?.trim()) {
    return (
      <div className="stations-list-page">
        <div className="stations-list-card">
          <Alert variant="info" className="mb-0">No station groups are assigned to your account.</Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="section_main_div stations-list-page">
      {error && (
        <Alert variant="danger" dismissible className="stations-list-alert" onClose={() => dispatch(clearStationGroupsError())}>
          {error}
        </Alert>
      )}

      <div className="stations-list-card">
          {loading ? (
          <div className="stations-list-loading">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted mb-0">Loading stations...</p>
          </div>
        ) : (
            <>
            <div className="stations-list-search mb-3">
              <Form.Control
                type="search"
                placeholder="Search by alias or station name..."
                value={aliasSearch}
                onChange={(e) => setAliasSearch(e.target.value)}
                className="stations-list-search-input"
                style={{ maxWidth: 320 }}
              />
              <SearchIcon className="stations-list-search-icon text-muted" sx={{ fontSize: 20 }} />
            </div>
            <DataTable
            columns={columns}
            data={filteredUsers}
            customStyles={tableCustomStyles}
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 25, 50]}
            noDataComponent={
              <div className="stations-list-empty">
                <RadioIcon sx={{ fontSize: 48 }} className="text-muted" />
                <p className="mt-2 mb-0">No stations found for your allotted groups.</p>
              </div>
            }
            paginationComponentOptions={{ rowsPerPageText: 'Rows per page:', rangeSeparatorText: 'of' }}
            />
            </>
          )}
        </div>
    </div>
  );
};

export default StationGroups;
